import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { servicesAPI, bookingsAPI } from '../utils/api';
import { useAuthStore } from '../store/authStore';
import RoomIcon from '../components/RoomIcon';
import '../styles/ConfigureService.css';

const ConfigureService = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const languageKey = i18n.resolvedLanguage?.startsWith('fr') ? 'fr' : 'en';
  const { isAuthenticated, user } = useAuthStore();
  const [service, setService] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadService = async () => {
      try {
        const response = serviceId
          ? await servicesAPI.getById(serviceId)
          : await servicesAPI.getAll();
        const loadedService = serviceId ? response.data : response.data[0];
        if (!loadedService) throw new Error('Service not found');
        setService(loadedService);
        const defaultQuantities = Object.fromEntries(
          (loadedService.configurationItems || []).map((item) => [item._id, item.defaultQuantity || 0])
        );
        if (isAuthenticated) {
          try {
            const preferenceResponse = await bookingsAPI.getPreferences();
            const preferences = preferenceResponse.data;
            if (preferences?.serviceId === loadedService._id) {
              setQuantities({ ...defaultQuantities, ...(preferences.optionQuantities || {}) });
            } else {
              setQuantities(defaultQuantities);
            }
          } catch {
            setQuantities(defaultQuantities);
          }
        } else {
          setQuantities(defaultQuantities);
        }
      } catch (requestError) {
        setError(t('services.loadError'));
      } finally {
        setLoading(false);
      }
    };
    loadService();
  }, [isAuthenticated, serviceId, t, user?._id]);

  const totals = useMemo(() => {
    if (!service) return { price: 0, minutes: 0 };
    let price = Number(service.basePrice);
    let minutes = Math.round(Number(service.duration) * 60);
    (service.configurationItems || []).forEach((item) => {
      const quantity = quantities[item._id] ?? item.defaultQuantity ?? 0;
      const additional = Math.max(0, quantity - (item.defaultQuantity || 0));
      price += additional * Number(item.pricePerAdditional || 0);
      minutes += additional * Number(item.minutesPerAdditional || 0);
    });
    return { price, minutes };
  }, [quantities, service]);

  const changeQuantity = (item, change) => {
    setQuantities((current) => ({
      ...current,
      [item._id]: Math.min(30, Math.max(
        item.defaultQuantity || 0,
        (current[item._id] ?? item.defaultQuantity ?? 0) + change
      ))
    }));
  };

  const continueToBooking = () => {
    const params = new URLSearchParams({
      serviceId: service._id,
      config: JSON.stringify(quantities)
    });
    navigate(`/booking?${params.toString()}`);
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error || !service) return <div className="error">{error || t('services.loadError')}</div>;

  return (
    <div className="configure-service-page">
      <div className="configure-service-heading">
        <p>{t('services.configurationEyebrow')}</p>
        <h1>{service.name?.[languageKey]}</h1>
        <span>{t('services.configurationInstructions')}</span>
      </div>

      <div className="configuration-layout">
        <div className="configuration-options-card">
          <div className="configuration-options-header">
            <span>{t('services.roomOrOption')}</span>
            <span>{t('services.quantity')}</span>
          </div>
          {(service.configurationItems || []).map((item) => {
            const quantity = quantities[item._id] ?? item.defaultQuantity ?? 0;
            return (
              <div className={`configuration-option ${item.defaultQuantity > 0 ? 'is-included' : ''} ${item.defaultQuantity === 0 && quantity > 0 ? 'is-selected' : ''}`} key={item._id}>
                <RoomIcon item={item} />
                <div className="configuration-option-copy">
                  <strong>{item.name?.[languageKey]}</strong>
                  <small>
                    {item.defaultQuantity > 0 && `${t('services.includedInBase', { count: item.defaultQuantity })} · `}
                    +${item.pricePerAdditional}
                  </small>
                </div>
                <div className="quantity-stepper" aria-label={item.name?.[languageKey]}>
                  <button type="button" onClick={() => changeQuantity(item, -1)} disabled={quantity <= item.defaultQuantity} aria-label={t('services.decreaseQuantity')}>−</button>
                  <output>{quantity}</output>
                  <button type="button" onClick={() => changeQuantity(item, 1)} aria-label={t('services.increaseQuantity')}>+</button>
                </div>
              </div>
            );
          })}
        </div>

        <aside className="configuration-total-card">
          <h2>{t('services.yourCleaning')}</h2>
          <div><span>{t('services.totalPrice')}</span><strong>${totals.price}</strong></div>
          <button type="button" onClick={continueToBooking}>{t('services.chooseDate')}</button>
          <small>{t('services.totalUpdatesAutomatically')}</small>
        </aside>
      </div>

      <section className="repeat-cleaning-rates">
        <h2>{t('services.repeatCleanings')}</h2>
        <div className="repeat-rate-header"><span>{t('services.frequency')}</span><span>{t('services.discount')}</span></div>
        <div><span>{t('services.within30Days')}</span><strong>−5%</strong></div>
        <div><span>{t('services.within15Days')}</span><strong>−10%</strong></div>
        <div><span>{t('services.weekly')}</span><strong>−15%</strong></div>
        <small>{t('services.discountAutomatic')}</small>
      </section>
    </div>
  );
};

export default ConfigureService;
